import { BaseEffectModule } from '../BaseEffectModule.js';
import { touchState } from '../methods.js';

let reinitializeScheduled = false;

/**
 * Mouse Distance Module - COMPLETE with Built-in Viewport Optimization
 * ✅ ALL ORIGINAL FEATURES PRESERVED
 * ✅ Added viewport optimization for performance
 * ✅ Smart mobile detection with full fallback system
 * ✅ Complete mobile fallback: intersection, scrollArea, disabled
 * ✅ Perfect delegation to BasicAnimationsModule
 * ✅ All distance calculation methods preserved
 * ✅ Full cleanup and linking functionality
 */
export class MouseDistanceModule extends BaseEffectModule {
    constructor() {
        super('mouseDistance', {
            version: '2.3.0',
            description: 'Distance-based effects with mobile fallback and viewport optimization',
            dependencies: ['basicAnimations']
        });
        
        this.falloffFunctions = {
            linear: (t) => t,
            quadratic: (t) => t * t,
            cubic: (t) => t * t * t,
            exponential: (t) => Math.pow(t, 2.2)
        };
        
        // ✅ NEW: Built-in viewport optimization (always enabled for performance)
        this.VIEWPORT_OPTIMIZATION = {
            enabled: true,
            buffer: 100,
            includeElementSize: true,
            checkInterval: 100,
            debugMode: false
        };
    }
    
    createState(element, config) {
        const mouseConfig = config.eventsConfig?.triggerSettings?.mousePosition || {};
        const deviceInfo = this.analyzeDevice();
        
        if (deviceInfo.isMobile && mouseConfig.mobileFallback?.enabled) {
            // mobileFallback is enabled
            return this.createMobileFallbackState(element, mouseConfig.mobileFallback, deviceInfo);
        } else if (deviceInfo.isMobile && !mouseConfig.mobileFallback?.enabled) {
            // mobileFallback is disabled
            return {
                enabled: false,
                isMobile: true,
                reason: 'Mobile device without fallback enabled'
            };
        }
        
        // desktop - normal distance state with viewport optimization
        return this.createDesktopDistanceState(element, mouseConfig);
    }
    
    /**
     * ✅ PRESERVED: Enhanced device analysis
     */
    analyzeDevice() {
        const hasTouch = touchState().hasTouch;
        const hasMouseSupport = touchState().hasMouseSupport;
        
        const isLikelyMobile = hasTouch && !hasMouseSupport;
        const isDesktop = !hasTouch && hasMouseSupport;
        
        return {
            isMobile: isLikelyMobile,
            isDesktop: isDesktop,
            hasTouch: hasTouch,
            hasMouseSupport: hasMouseSupport,
            userAgent: navigator.userAgent,
            deviceType: isLikelyMobile ? 'mobile' : 'desktop'
        };
    }
    
    /**
     * ✅ PRESERVED: Create mobile fallback state (ALL TYPES)
     */
    createMobileFallbackState(element, fallbackConfig, deviceInfo) {
        const fallbackType = fallbackConfig.fallbackType || 'intersection';
        const settings = fallbackConfig.fallbackSettings || {};
        
        // console.log(`📱 Creating mobile fallback (${fallbackType}) for ${element.id}`);
        
        switch (fallbackType) {
            case 'intersection':
                return this.setupIntersectionFallback(element, settings, deviceInfo);
                
            case 'scrollArea':
                return this.setupScrollAreaFallback(element, settings, deviceInfo);
                
            case 'disabled':
                return {
                    enabled: false,
                    isMobileFallback: true,
                    fallbackType: 'disabled',
                    deviceInfo: deviceInfo,
                    reason: 'Disabled on mobile by user choice'
                };
                
            default:
                console.warn(`Unknown mobile fallback type: ${fallbackType}`);
                return {
                    enabled: false,
                    isMobileFallback: true,
                    reason: `Unknown fallback type: ${fallbackType}`
                };
        }
    }
    
    /**
     * ✅ PRESERVED: Setup Intersection Observer fallback
     */
    setupIntersectionFallback(element, settings, deviceInfo) {
        let hasTriggered = false;
        
        const observerCallback = (entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && (!settings.once || !hasTriggered)) {
                    hasTriggered = true;
                    this.triggerMobileFallbackAnimation(element, 'intersection');
                    
                    if (settings.once) {
                        observer.unobserve(element);
                    }
                } else if (!entry.isIntersecting && !settings.once && hasTriggered) {
                    hasTriggered = false;
                    this.reverseMobileFallbackAnimation(element, 'intersection');
                }
            });
        };
        
        const observer = new IntersectionObserver(observerCallback, {
            threshold: (settings.threshold || 50) / 100,
            rootMargin: `${-(settings.offset || 0)}px 0px 0px 0px`
        });
        
        observer.observe(element);
        
        return {
            enabled: true,
            isMobileFallback: true,
            fallbackType: 'intersection',
            deviceInfo: deviceInfo,
            observer: observer,
            settings: settings,
            hasTriggered: hasTriggered,
            currentProgress: 0
        };
    }
    
    /**
     * ✅ PRESERVED: Setup Scroll Area fallback
     */
    setupScrollAreaFallback(element, settings, deviceInfo) {
        // Use ScrollProgressCalculator for animation area on mobile
        let progressCalculator = null;
        
        // Dynamically import ScrollProgressCalculator
        if (typeof window !== 'undefined' && window.ScrollProgressCalculator) {
            progressCalculator = new window.ScrollProgressCalculator(element, {
                animationStart: settings.animationStart || 0,
                animationEnd: settings.animationEnd || 100,
                scrollDirection: 'vertical'
            });
            
            progressCalculator.onProgress((progress) => {
                this.updateMobileFallbackProgress(element, progress);
            });
        } else {
            console.warn('ScrollProgressCalculator not available for mobile fallback');
            // Fallback to simple scroll listener
            this.setupSimpleScrollFallback(element, settings);
        }
        
        return {
            enabled: true,
            isMobileFallback: true,
            fallbackType: 'scrollArea',
            deviceInfo: deviceInfo,
            progressCalculator: progressCalculator,
            settings: settings,
            currentProgress: 0
        };
    }
    
    /**
     * ✅ PRESERVED: Setup simple scroll fallback (when ScrollProgressCalculator not available)
     */
    setupSimpleScrollFallback(element, settings) {
        const scrollHandler = () => {
            const rect = element.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            
            // Simple progress calculation
            const elementTop = rect.top;
            const elementHeight = rect.height;
            
            let progress = 0;
            if (elementTop < windowHeight && elementTop + elementHeight > 0) {
                // Element is in viewport
                const visibleRatio = Math.max(0, Math.min(1, 
                    (windowHeight - elementTop) / (windowHeight + elementHeight)
                ));
                progress = visibleRatio;
            }
            
            this.updateMobileFallbackProgress(element, progress);
        };
        
        window.addEventListener('scroll', scrollHandler, { passive: true });
        
        // Store cleanup function
        element._mobileScrollCleanup = () => {
            window.removeEventListener('scroll', scrollHandler);
        };
    }
    
    /**
     * ✅ ENHANCED: Create desktop distance state with viewport optimization
     */
    createDesktopDistanceState(element, mouseConfig) {
        // Check parallax conflicts
        const hasMouseParallax = element.classList.contains('unified-mouse-parallax') || 
                                 element.dataset.useMouseParallax === 'true';
        const hasScrollParallax = element.classList.contains('unified-scroll-parallax') || 
                                  element.dataset.useParallax === 'true';
        
        if (hasMouseParallax || hasScrollParallax) {
            // console.log(`🤝 Mouse Distance + Parallax detected for ${element.id} - will coordinate effects`);
        }
        
        const state = {
            enabled: true,
            isDesktop: true,
            options: mouseConfig,
            
            // Progress tracking
            currentProgress: 0,
            targetProgress: 0,
            lastProgress: 0,
            
            // ✅ NEW: Viewport optimization state
            viewportState: {
                isInAnimationZone: true,        // Initially assume visible
                lastViewportCheck: 0,           // Last viewport check timestamp
                animationZoneRect: null,        // Cached animation zone bounds
                wasOutOfZone: false,            // Track state changes
                checksDue: 0                    // Performance: skip checks counter
            },
            
            // Linking to BasicAnimationsModule
            basicAnimationState: null,
            
            // Performance optimization
            lastDistance: -1,
            lastUpdate: 0,
            updateThreshold: 16, // ~60fps
            
            // Element cached data
            effectiveMaxDistance: null,
            elementCenter: { x: 0, y: 0 },
            elementRect: { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 },
            
            // Conflict resolution
            hasParallaxConflict: hasMouseParallax || hasScrollParallax,
            controlledProperties: []
        };
        
        // Calculate effective max distance
        state.effectiveMaxDistance = this.calculateEffectiveMaxDistance(element, state.options);
        
        // ✅ NEW: Initialize viewport optimization
        this.initializeViewportOptimization(element, state);
        
        // console.log(`🧲 Desktop mouse distance state created (viewport opt: ON)`);
        return state;
    }
    
    /**
     * ✅ NEW: Initialize viewport optimization
     */
    initializeViewportOptimization(element, state) {
        const viewportState = state.viewportState;
        
        // Calculate initial animation zone
        this.updateAnimationZone(element, state);
        
        // Mark element for viewport tracking
        element.classList.add('viewport-optimized-distance');
    }
    
    /**
     * ✅ NEW: Update animation zone calculation
     */
    updateAnimationZone(element, state) {
        const viewportState = state.viewportState;
        const vpOpt = this.VIEWPORT_OPTIMIZATION;
        
        const rect = element.getBoundingClientRect();
        const viewport = {
            width: window.innerWidth,
            height: window.innerHeight
        };
        
        // Calculate effective buffer including element size
        let effectiveBuffer = vpOpt.buffer;
        if (vpOpt.includeElementSize) {
            effectiveBuffer += Math.max(rect.width, rect.height);
        }
        
        // Calculate animation zone bounds
        const animationZone = {
            left: -effectiveBuffer,
            top: -effectiveBuffer,
            right: viewport.width + effectiveBuffer,
            bottom: viewport.height + effectiveBuffer
        };
        
        // Check if element intersects with animation zone
        const isInZone = !(
            rect.right < animationZone.left ||
            rect.left > animationZone.right ||
            rect.bottom < animationZone.top ||
            rect.top > animationZone.bottom
        );
        
        // Update state
        const wasInZone = viewportState.isInAnimationZone;
        viewportState.isInAnimationZone = isInZone;
        viewportState.animationZoneRect = animationZone;
        
        // Track major state changes for optimization
        if (wasInZone && !isInZone) {
            viewportState.wasOutOfZone = true;
        } else if (!wasInZone && isInZone) {
            viewportState.wasOutOfZone = false;
        }
    }
    
    /**
     * ✅ NEW: Check if element should be animated (viewport optimization)
     */
    shouldAnimateElement(element, state, currentTime) {
        const viewportState = state.viewportState;
        const vpOpt = this.VIEWPORT_OPTIMIZATION;
        
        // ✅ PERFORMANCE: Skip viewport checks when element is stable out of zone
        if (viewportState.wasOutOfZone && viewportState.checksDue > 0) {
            viewportState.checksDue--;
            return false; // Still out of zone, skip expensive checks
        }
        
        // ✅ PERFORMANCE: Update viewport check periodically
        if (currentTime - viewportState.lastViewportCheck > vpOpt.checkInterval) {
            this.updateAnimationZone(element, state);
            viewportState.lastViewportCheck = currentTime;
            
            // ✅ OPTIMIZATION: If element went out of zone, delay future checks
            if (!viewportState.isInAnimationZone) {
                viewportState.checksDue = 5; // Skip next 5 checks (~500ms)
            }
        }
        
        return viewportState.isInAnimationZone;
    }
    
    /**
     * ✅ ENHANCED: Update method with viewport optimization + ALL MOBILE FEATURES
     */
    update(element, state, sharedState, deltaTime, cache) {
        if (!this.isEnabled || !state.enabled) return;
        
        // Handle mobile fallback states
        if (state.isMobileFallback) {
            return this.updateMobileFallback(element, state, sharedState, deltaTime, cache);
        }
        
        // ✅ NEW: Desktop with viewport optimization
        return this.updateDesktopDistance(element, state, sharedState, deltaTime, cache);
    }
    
    /**
     * ✅ PRESERVED: Update mobile fallback
     */
    updateMobileFallback(element, state, sharedState, deltaTime, cache) {
        // Mobile fallbacks are handled by their respective observers/calculators
        // This method serves as a placeholder for any additional mobile-specific logic
        
        // Update progress for scroll area fallback
        if (state.fallbackType === 'scrollArea' && state.progressCalculator) {
            // Progress calculation is handled by the calculator's callback
            // We just need to ensure the element data is updated
        }
        
        // Performance tracking for mobile
        if (sharedState.timestamp - state.lastUpdate > 100) { // 10fps for mobile
            state.lastUpdate = sharedState.timestamp;
        }
    }
    
    /**
     * ✅ ENHANCED: Desktop distance update with viewport optimization
     */
    updateDesktopDistance(element, state, sharedState, deltaTime, cache) {
        // ✅ NEW: Viewport optimization early exit
        if (!this.shouldAnimateElement(element, state, sharedState.timestamp)) {
            // Element is outside animation zone - skip expensive calculations
            // Optionally reset to initial state when leaving zone
            if (state.currentProgress > 0) {
                state.targetProgress = 0;
                state.currentProgress = 0;
                this.delegateProgressToAnimationSystem(element, 0);
            }
            return;
        }
        
        // Performance throttling
        if (sharedState.timestamp - state.lastUpdate < state.updateThreshold) {
            return;
        }
        
        // Update element geometry from cache
        if (cache.rect) {
            // Update center
            state.elementCenter.x = cache.rect.left + cache.rect.width / 2;
            state.elementCenter.y = cache.rect.top + cache.rect.height / 2;
            
            // Update full rect for edge calculations
            state.elementRect.left = cache.rect.left;
            state.elementRect.top = cache.rect.top;
            state.elementRect.right = cache.rect.right;
            state.elementRect.bottom = cache.rect.bottom;
            state.elementRect.width = cache.rect.width;
            state.elementRect.height = cache.rect.height;
        }
        
        // Calculate distance based on mode
        const distance = this.calculateDistanceByMode(state, sharedState.mousePosition);
        
        // Skip if distance hasn't changed significantly
        if (Math.abs(distance - state.lastDistance) < 3) {
            return;
        }
        
        const progress = this.distanceToProgress(distance, state.options, state.effectiveMaxDistance);
        
        // Update progress if significant change
        if (this.isSignificantChange(state.targetProgress, progress)) {
            state.targetProgress = progress;
            
            // Delegate to animation system
            this.delegateProgressToAnimationSystem(element, progress);
        }
        
        state.lastDistance = distance;
        state.lastUpdate = sharedState.timestamp;
    }
    
    /**
     * ✅ PRESERVED: Trigger mobile fallback animation
     */
    triggerMobileFallbackAnimation(element, fallbackType) {
        // console.log(`📱 Triggering mobile fallback animation (${fallbackType}) for ${element.id}`);
        
        // Delegate to animation system with progress 1
        this.delegateProgressToAnimationSystem(element, 1);
    }
    
    /**
     * ✅ PRESERVED: Reverse mobile fallback animation
     */
    reverseMobileFallbackAnimation(element, fallbackType) {
        // console.log(`📱 Reversing mobile fallback animation (${fallbackType}) for ${element.id}`);
        
        // Delegate to animation system with progress 0
        this.delegateProgressToAnimationSystem(element, 0);
    }
    
    /**
     * ✅ PRESERVED: Update mobile fallback progress
     */
    updateMobileFallbackProgress(element, progress) {
        // Delegate to animation system
        this.delegateProgressToAnimationSystem(element, progress);
    }
    
    /**
     * ✅ PRESERVED: Delegate progress to animation system
     */
    delegateProgressToAnimationSystem(element, progress) {
        if (window.animationSystem) {
            const elementData = window.animationSystem.elements.get(element);

            // reinitialize page when element is missing ( working with barba etc )
            if (!elementData) {
                // if (!reinitializeScheduled) {
                //     reinitializeScheduled = true;

                //     // console.log('🔄 Element missing from system - re-registering');
                    
                //     if (window.offScrollAnimations?.reinitialize) {
                //         window.offScrollAnimations.reinitialize();
                //     }
                // }
                // return;

                // if (!elementData) {
                //     // ✅ PROSTE: Po prostu zarejestruj element na nowo
                //     try {
                //         const rawData = element.dataset.gsapAnimation;
                //         if (rawData) {
                //             const animationData = JSON.parse(rawData);
                //             window.animationSystem.registerElement(element, animationData);
                //             console.log('✅ Element registered on-demand');
                //         }
                //     } catch (error) {
                //         console.warn('⚠️ Failed to register element:', error);
                //     }
                //     return; // Skip this frame
                // }
            }

            if (elementData) {
                // Basic animations
                const basicEffect = elementData.activeEffects.get('basicAnimations');
                if (basicEffect?.module?.setProgress) {
                    basicEffect.module.setProgress(basicEffect.state, progress);
                }
                
                // Text animations
                const textEffect = elementData.activeEffects.get('textAnimations');
                if (textEffect?.module?.setProgress && textEffect.state?.enabled) {
                    textEffect.module.setProgress(textEffect.state, progress);
                }
            }
        }
    }
    
    /**
     * ✅ PRESERVED: Distance calculation methods (ALL)
     */
    calculateDistanceByMode(state, mousePos) {
        if (state.options.distanceMode === 'edge') {
            return this.calculateDistanceToEdge(state.elementRect, mousePos);
        } else {
            return this.calculateDistanceToCenter(state.elementCenter, mousePos);
        }
    }
    
    calculateDistanceToCenter(center, mousePos) {
        const deltaX = mousePos.x - center.x;
        const deltaY = mousePos.y - center.y;
        return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    }
    
    calculateDistanceToEdge(rect, mousePos) {
        const { x: mouseX, y: mouseY } = mousePos;
        const { left, top, right, bottom } = rect;
        
        // Check if mouse is inside the element
        if (mouseX >= left && mouseX <= right && mouseY >= top && mouseY <= bottom) {
            return 0; // Mouse is inside, distance is 0
        }
        
        // Find closest point on the rectangle's edge
        const closestX = Math.max(left, Math.min(right, mouseX));
        const closestY = Math.max(top, Math.min(bottom, mouseY));
        
        // Calculate distance to closest edge point
        const deltaX = mouseX - closestX;
        const deltaY = mouseY - closestY;
        
        return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    }
    
    distanceToProgress(distance, options, effectiveMaxDistance) {
        const clampedDistance = Math.max(
            options.minDistance, 
            Math.min(effectiveMaxDistance, distance)
        );
        
        // Calculate progress (0 = far, 1 = close)
        let progress = 1 - ((clampedDistance - options.minDistance) / 
                           (effectiveMaxDistance - options.minDistance));
        
        // Apply sensitivity
        progress = Math.pow(progress, 1 / options.sensitivity);
        
        // Apply falloff curve
        const falloffFunction = this.falloffFunctions[options.falloffType] || this.falloffFunctions.quadratic;
        progress = falloffFunction(progress);
        
        return this.clamp(progress, 0, 1);
    }
    
    calculateEffectiveMaxDistance(element, options) {
        if (options.maxDistance && options.maxDistance > 0) {
            return options.maxDistance;
        }
        
        // Calculate full window max distance
        const rect = element.getBoundingClientRect();
        
        let referencePoint;
        if (options.distanceMode === 'edge') {
            // For edge mode, use element center as reference for max distance calculation
            referencePoint = {
                x: rect.left + rect.width / 2,
                y: rect.top + rect.height / 2
            };
        } else {
            // For center mode, use center as reference
            referencePoint = {
                x: rect.left + rect.width / 2,
                y: rect.top + rect.height / 2
            };
        }
        
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        
        // Distance to farthest corner
        const distanceToCorners = [
            Math.sqrt(referencePoint.x * referencePoint.x + referencePoint.y * referencePoint.y),
            Math.sqrt((windowWidth - referencePoint.x) * (windowWidth - referencePoint.x) + referencePoint.y * referencePoint.y),
            Math.sqrt(referencePoint.x * referencePoint.x + (windowHeight - referencePoint.y) * (windowHeight - referencePoint.y)),
            Math.sqrt((windowWidth - referencePoint.x) * (windowWidth - referencePoint.x) + (windowHeight - referencePoint.y) * (windowHeight - referencePoint.y))
        ];
        
        return Math.max(...distanceToCorners);
    }
    
    /**
     * ✅ PRESERVED: Link to basic animations with mobile awareness
     */
    linkBasicAnimations(state, basicState) {
        state.basicAnimationState = basicState;
        
        if (state.isMobileFallback) {
            // For mobile fallback, we control all properties
            state.controlledProperties = ['opacity', 'x', 'y', 'scale', 'rotation', 'blur'];
            // console.log(`🔗 Mobile fallback linked to basic animations`);
        } else {
            // Desktop behavior - determine controlled properties
            if (basicState && basicState.initialProps && basicState.finalProps) {
                const allProps = new Set([
                    ...Object.keys(basicState.initialProps),
                    ...Object.keys(basicState.finalProps)
                ]);
                
                // Conflict resolution for parallax
                state.controlledProperties = Array.from(allProps).filter(prop => {
                    if (state.hasParallaxConflict && (prop === 'x' || prop === 'y')) {
                        return false; // Parallax controls position
                    }
                    return true;
                });
            }
            // console.log(`🔗 Desktop mouse distance linked to basic animations, controlling:`, state.controlledProperties);
        }
    }
    
    /**
     * ✅ ENHANCED: Get comprehensive state info including mobile + viewport
     */
    getStateInfo(state, sharedState) {
        const baseInfo = {
            enabled: state.enabled,
            isMobileFallback: state.isMobileFallback || false,
            deviceInfo: state.deviceInfo || null,
            fallbackType: state.fallbackType || null
        };
        
        if (state.isMobileFallback) {
            return {
                ...baseInfo,
                currentProgress: state.currentProgress || 0,
                settings: state.settings || {},
                hasTriggered: state.hasTriggered || false
            };
        } else if (state.isDesktop) {
            const currentDistance = this.getCurrentDistance(state, sharedState);
            
            return {
                ...baseInfo,
                currentProgress: state.currentProgress,
                targetProgress: state.targetProgress,
                currentDistance: currentDistance,
                effectiveMaxDistance: state.effectiveMaxDistance,
                distanceMode: state.options.distanceMode,
                hasParallaxConflict: state.hasParallaxConflict,
                controlledProperties: state.controlledProperties,
                isLinked: !!state.basicAnimationState,
                options: state.options,
                
                // ✅ NEW: Viewport optimization info
                viewportOptimization: {
                    enabled: true,
                    isInAnimationZone: state.viewportState?.isInAnimationZone || true,
                    lastCheck: state.viewportState?.lastViewportCheck || 0,
                    animationZone: state.viewportState?.animationZoneRect || null,
                    buffer: this.VIEWPORT_OPTIMIZATION.buffer,
                    includeElementSize: this.VIEWPORT_OPTIMIZATION.includeElementSize
                },
                
                elementGeometry: {
                    center: { ...state.elementCenter },
                    rect: { ...state.elementRect }
                }
            };
        }
        
        return baseInfo;
    }
    
    getCurrentDistance(state, sharedState) {
        if (state.isMobileFallback) {
            return 0; // No distance concept on mobile
        }
        return this.calculateDistanceByMode(state, sharedState.mousePosition);
    }
    
    /**
     * ✅ PRESERVED: Cleanup with mobile fallback + viewport optimization support
     */
    cleanup(state) {
        // Cleanup mobile fallback resources
        if (state.isMobileFallback) {
            if (state.observer) {
                state.observer.disconnect();
                state.observer = null;
            }
            
            if (state.progressCalculator && state.progressCalculator.destroy) {
                state.progressCalculator.destroy();
                state.progressCalculator = null;
            }
            
            // Cleanup simple scroll fallback
            if (state.element && state.element._mobileScrollCleanup) {
                state.element._mobileScrollCleanup();
                delete state.element._mobileScrollCleanup;
            }
        }
        
        // ✅ NEW: Cleanup viewport optimization
        if (state.element) {
            state.element.classList.remove('viewport-optimized-distance');
        }
        
        // Standard cleanup
        state.basicAnimationState = null;
        state.options = null;
        state.controlledProperties = [];
        state.deviceInfo = null;
        state.viewportState = null; // ✅ NEW
        
        // console.log(`🗑️ Mouse distance cleaned up (mobile: ${state.isMobileFallback || false}, viewport opt: ON)`);
    }
}